import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Login } from "./routes/Login";
import { Layout } from "./components/Layout";
import { PlantsList } from "./routes/PlantsList";
import { PlantNew } from "./routes/PlantNew";
import { PlantDetail } from "./routes/PlantDetail";
import { PlantEdit } from "./routes/PlantEdit";
import { MapPage } from "./routes/MapPage";
import { Today } from "./routes/Today";
import { Tag } from "./routes/Tag";
import { More } from "./routes/More";
import { Tasks } from "./routes/Tasks";
import { Observations } from "./routes/Observations";
import { CareNotes } from "./routes/CareNotes";
import { CurrentGardenProvider } from "./lib/useCurrentGarden";
import { supabase, type AuthSession } from "./auth";

type AuthState =
  | { status: "loading" }
  | { status: "authed"; session: AuthSession }
  | { status: "anon" };

/** Where "/" lands. Mobile gets /today (the muddy-gloves screen);
 *  desktop keeps the previous /plants default. */
function defaultRoute(): string {
  if (typeof window === "undefined" || !window.matchMedia) return "/plants";
  return window.matchMedia("(max-width: 700px)").matches ? "/today" : "/plants";
}

export function App() {
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    if (!supabase) {
      setAuth({ status: "anon" });
      return;
    }

    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setAuth(
        data.session ? { status: "authed", session: data.session } : { status: "anon" },
      );
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuth(session ? { status: "authed", session } : { status: "anon" });
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (auth.status === "loading") {
    return <div style={{ padding: 32, fontFamily: "var(--font-body)" }}>Loading…</div>;
  }

  if (auth.status === "anon") {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const home = defaultRoute();

  return (
    <CurrentGardenProvider>
      <Routes>
        <Route path="/login" element={<Navigate to={home} replace />} />
        <Route element={<Layout session={auth.session} />}>
          <Route path="/" element={<Navigate to={home} replace />} />
          <Route path="/today" element={<Today />} />
          <Route path="/tag" element={<Tag />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/plants" element={<PlantsList />} />
          <Route path="/plants/new" element={<PlantNew />} />
          <Route path="/plants/:id" element={<PlantDetail />} />
          <Route path="/plants/:id/edit" element={<PlantEdit />} />
          <Route path="/plants/:id/care-notes" element={<CareNotes />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/observations" element={<Observations />} />
          <Route path="/more" element={<More session={auth.session} />} />
          <Route path="*" element={<Navigate to={home} replace />} />
        </Route>
      </Routes>
    </CurrentGardenProvider>
  );
}
